/**
 * Order service types and interfaces
 */

export interface Order {
  id: string;
  orderNumber: string;
  customer: OrderCustomer;
  items: OrderItem[];
  shipping: OrderShipping;
  billing: OrderBilling;
  payment: OrderPayment;
  status: OrderStatus;
  total: OrderTotal;
  notes?: string;
  tags?: string[];
  createdAt: string;
  updatedAt: string;
  shippedAt?: string;
  deliveredAt?: string;
  cancelledAt?: string;
}

export interface OrderCustomer {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  phone?: string;
  company?: string;
}

export interface OrderItem {
  id: string;
  productId: string;
  productName: string;
  productSku: string;
  quantity: number;
  unitPrice: number;
  totalPrice: number;
  discount?: number;
  tax?: number;
  specifications?: Record<string, any>;
}

export interface OrderShipping {
  method: ShippingMethod;
  address: OrderAddress;
  trackingNumber?: string;
  estimatedDelivery?: string;
  actualDelivery?: string;
  cost: number;
  notes?: string;
}

export interface OrderBilling {
  address: OrderAddress;
  method: BillingMethod;
  notes?: string;
}

export interface OrderAddress {
  firstName: string;
  lastName: string;
  company?: string;
  address1: string;
  address2?: string;
  city: string;
  state: string;
  postalCode: string;
  country: string;
  phone?: string;
}

export interface OrderPayment {
  method: PaymentMethod;
  status: PaymentStatus;
  transactionId?: string;
  amount: number;
  currency: string;
  processedAt?: string;
  refundedAt?: string;
  refundAmount?: number;
}

export interface OrderTotal {
  subtotal: number;
  tax: number;
  shipping: number;
  discount: number;
  total: number;
  currency: string;
}

export enum OrderStatus {
  PENDING = "pending",
  CONFIRMED = "confirmed",
  PROCESSING = "processing",
  SHIPPED = "shipped",
  DELIVERED = "delivered",
  CANCELLED = "cancelled",
  RETURNED = "returned",
  REFUNDED = "refunded",
}

export enum ShippingMethod {
  STANDARD = "standard",
  EXPRESS = "express",
  OVERNIGHT = "overnight",
  PICKUP = "pickup",
}

export enum BillingMethod {
  INVOICE = "invoice",
  CREDIT_CARD = "credit_card",
  BANK_TRANSFER = "bank_transfer",
  PAYPAL = "paypal",
}

export enum PaymentMethod {
  CREDIT_CARD = "credit_card",
  DEBIT_CARD = "debit_card",
  BANK_TRANSFER = "bank_transfer",
  PAYPAL = "paypal",
  INVOICE = "invoice",
  CASH = "cash",
}

export enum PaymentStatus {
  PENDING = "pending",
  PROCESSING = "processing",
  COMPLETED = "completed",
  FAILED = "failed",
  CANCELLED = "cancelled",
  REFUNDED = "refunded",
}

export interface CreateOrderPayload {
  customerId: string;
  items: Array<{
    productId: string;
    quantity: number;
    specifications?: Record<string, any>;
  }>;
  shipping: {
    method: ShippingMethod;
    address: OrderAddress;
    cost?: number;
    notes?: string;
  };
  billing: {
    address: OrderAddress;
    method: BillingMethod;
    notes?: string;
  };
  payment: {
    method: PaymentMethod;
    amount: number;
    currency?: string;
  };
  notes?: string;
  tags?: string[];
}

export interface UpdateOrderPayload {
  status?: OrderStatus;
  shipping?: Partial<OrderShipping>;
  billing?: Partial<OrderBilling>;
  payment?: Partial<OrderPayment>;
  notes?: string;
  tags?: string[];
}

export interface OrderSearchParams {
  orderNumber?: string;
  customerId?: string;
  status?: OrderStatus;
  paymentStatus?: PaymentStatus;
  dateFrom?: string;
  dateTo?: string;
  minTotal?: number;
  maxTotal?: number;
  tags?: string[];
  page?: number;
  limit?: number;
  sortBy?: "createdAt" | "updatedAt" | "total" | "status";
  sortOrder?: "asc" | "desc";
}

export interface OrderResponse {
  order: Order;
  message: string;
  success: boolean;
}

export interface OrdersResponse {
  orders: Order[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
    hasNext: boolean;
    hasPrev: boolean;
  };
  message: string;
  success: boolean;
}

export interface OrderSearchResponse {
  orders: Order[];
  filters: {
    statuses: Array<{ status: OrderStatus; count: number }>;
    paymentStatuses: Array<{ status: PaymentStatus; count: number }>;
    dateRange: { from: string; to: string };
    totalRange: { min: number; max: number };
  };
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
    hasNext: boolean;
    hasPrev: boolean;
  };
  message: string;
  success: boolean;
}

export interface OrderStatusResponse {
  status: OrderStatus;
  message: string;
  success: boolean;
}

export interface OrderInvoiceResponse {
  invoice: {
    id: string;
    number: string;
    url: string;
    downloadUrl: string;
    createdAt: string;
  };
  message: string;
  success: boolean;
}

export interface OrderTrackingResponse {
  tracking: {
    number: string;
    status: string;
    carrier: string;
    events: Array<{
      date: string;
      status: string;
      location: string;
      description: string;
    }>;
    estimatedDelivery: string;
  };
  message: string;
  success: boolean;
}

export interface OrderAnalyticsResponse {
  analytics: {
    totalOrders: number;
    totalRevenue: number;
    averageOrderValue: number;
    statusBreakdown: Record<OrderStatus, number>;
    revenueByMonth: Array<{ month: string; revenue: number }>;
    topProducts: Array<{
      productId: string;
      productName: string;
      quantity: number;
      revenue: number;
    }>;
  };
  message: string;
  success: boolean;
}

export interface BulkOrderUpdatePayload {
  orderIds: string[];
  updates: Partial<UpdateOrderPayload>;
}

export interface OrderCancellationPayload {
  reason: string;
  notes?: string;
}
